<script setup lang="ts">
import {
    BBtn, BIcon, BTooltip,
} from '../base';
import {
    useClipboard, useTranslation, useJson,
} from '@/composables';

type PropsType = {
    code: unknown;
    language?: string;
    maxHeight?: number;
    copyable?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    language: 'json',
    maxHeight: 300,
    copyable: true,
});

const { t } = useTranslation();
const { copyToClipboard } = useClipboard();
const { formatJsonForDisplay } = useJson();

const formatCode = (data: unknown): string => {
    if (!data) {
        return '';
    }

    if (typeof data === 'string') {
        return data;
    }

    return formatJsonForDisplay(data);
};

const handleCopy = (): void => {
    copyToClipboard(props.code);
};
</script>

<template>
    <div
        class="code-block-container"
        :style="{ maxHeight: `${maxHeight}px` }"
    >
        <BTooltip
            v-if="copyable"
            content-class="bg-grey-700 text-body-2"
            location="start"
        >
            <template #activator="{ props: copyProps }">
                <BBtn
                    v-bind="copyProps"
                    icon
                    color="transparent"
                    class="copy-element"
                    density="comfortable"
                    @click="handleCopy"
                >
                    <BIcon
                        size="small"
                        color="white"
                    >mdi-content-copy</BIcon
                    >
                </BBtn>
            </template>
            {{ t('common.copy') }}
        </BTooltip>

        <pre class="code-content"><code>{{ formatCode(code) }}</code></pre>
    </div>
</template>

<style scoped lang="scss">
.code-block-container {
    position: relative;
    background-color: rgb(var(--v-theme-grey-900));
    border-radius: 8px;
    padding: 20px;
    overflow: auto;
}

.code-block-container:hover .copy-element {
    opacity: 1;
}

.copy-element {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 1;
    opacity: 0;
    transition: opacity 100ms ease;
}

.code-content {
    margin: 0;
    color: #d4d4d4;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Courier New', monospace;
    font-size: 13px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-all;
}
</style>
